/**
 * The post page's comments as threads (DESIGN.md "Comments sit in parts").
 *
 * Pure, no React: the rail, the sheets and scripts/test-comments.mjs all read
 * these. Today every comment is its own thread. Every new comment is for
 * everyone (SHARED); a concept comment written Team only before that rule
 * (INTERNAL) still shows to the team, never to the client. Brief comments are
 * flat and shared. Anchors, replies, done and
 * M7 changes arrive with backend #128 / #125 and are passed through untouched;
 * commentCapabilities decides what shows.
 */
import type {
  CommentAnchor,
  CommentThreadExtras,
  IdeaComment,
} from "@/types/contentCalendar.type";
import type { ApiComment, ApiCommentDraft, ApiThread } from "./commentApi";
import type { BriefComment } from "@/types/productionBrief.type";
import type { PostPhase } from "../components/PostHeader";
import { isDone, supportsResolve } from "./commentCapabilities";

export type CommentSource = "concept" | "brief";
export type CommentVisibility = "SHARED" | "INTERNAL";
export type CommentSendState = "sending" | "failed";

export interface CommentEntry {
  id: string;
  authorType: "TEAM" | "CLIENT";
  authorName: string;
  body: string;
  createdAt: string;
  /** Concept comments only; brief comments have no visibility today. */
  visibility?: CommentVisibility;
  /** A post from this page that is still going out, or that failed. */
  sendState?: CommentSendState;
  /** backend #128: the teammate who wrote it, so their own comment gets Edit and Delete. */
  authorUserId?: string | null;
  /** backend #128: the people it named, highlighted in the body. */
  mentions?: { kind: "user" | "client"; id: string; name: string }[];
  /** backend #128: when its author last edited it. */
  editedAt?: string | null;
  /** backend #128: deleted by its author; the body is withheld. */
  deleted?: boolean;
  /** M7's read of a client's comment for the team ("Shorten the hook"). Never sent to a guest. */
  intent?: string | null;
}

/** M7's change on a thread (backend #125): drafting, suggested, accepted, or failed. */
export interface ThreadM7Change {
  /** The draft's id; accept, dismiss and undo act on it. */
  id?: string;
  state: "drafting" | "suggested" | "accepted" | "failed";
  label: string;
  body: string;
  note?: string | null;
  error?: string | null;
}

export interface CommentThreadData {
  id: string;
  source: CommentSource;
  /** The concept or brief version the comment was written on. */
  version: number;
  comment: CommentEntry;
  /** backend #128 */
  anchor?: CommentAnchor | null;
  /** backend #128 */
  replies?: CommentEntry[];
  /** backend #128 */
  resolvedAt?: string | null;
  /** backend #125 */
  m7Change?: ThreadM7Change | null;
  /** backend #128: who marked it done. */
  resolvedByName?: string | null;
  /** The concept version id or the brief id the thread sits on (replies go there). */
  targetId?: string;
  /** backend #128: which shot, slide or channel the anchor names ("3", "instagram"). */
  anchorKey?: string | null;
}

/** Copies only the extras the API actually sent, so absent stays absent. */
function extrasOf(source: CommentThreadExtras) {
  const out: Pick<CommentThreadData, "anchor" | "replies" | "resolvedAt" | "m7Change"> = {};
  if (source.anchor !== undefined) out.anchor = source.anchor;
  if (source.replies !== undefined) out.replies = source.replies.map(entryOf);
  if (source.resolvedAt !== undefined) out.resolvedAt = source.resolvedAt;
  if (source.m7Change !== undefined) out.m7Change = source.m7Change;
  return out;
}

function entryOf(comment: IdeaComment | BriefComment): CommentEntry {
  const entry: CommentEntry = {
    id: comment.id,
    authorType: comment.authorType,
    authorName: comment.authorName,
    body: comment.body,
    createdAt: comment.createdAt,
  };
  if ("visibility" in comment && comment.visibility) entry.visibility = comment.visibility;
  return entry;
}

type VersionWithComments = { version: number; comments: IdeaComment[] };

/**
 * Every concept-version comment as a thread. The team reads all of them; the
 * client never reads a private one from before (INTERNAL).
 */
export function conceptThreads(
  versions: readonly VersionWithComments[],
  audience: "team" | "client" = "team",
): CommentThreadData[] {
  const threads: CommentThreadData[] = [];
  for (const version of versions) {
    for (const comment of version.comments) {
      if (audience === "client" && comment.visibility === "INTERNAL") continue;
      threads.push({
        id: `concept:${comment.id}`,
        source: "concept",
        version: version.version,
        comment: entryOf(comment),
        ...extrasOf(comment),
      });
    }
  }
  return threads;
}

/** Every brief comment as a thread, on the brief version it was written on. */
export function briefThreads(comments: readonly BriefComment[]): CommentThreadData[] {
  return comments.map((comment) => ({
    id: `brief:${comment.id}`,
    source: "brief",
    version: comment.briefVersion,
    comment: entryOf(comment),
    ...extrasOf(comment),
  }));
}

function time(iso: string): number {
  const value = Date.parse(iso);
  return Number.isNaN(value) ? 0 : value;
}

/** The newest moment anyone wrote on the thread. */
export function lastActivity(thread: CommentThreadData): number {
  let latest = time(thread.comment.createdAt);
  for (const reply of thread.replies ?? []) latest = Math.max(latest, time(reply.createdAt));
  return latest;
}

/**
 * Open threads in the order they were written, so the markers read 1, 2, 3
 * down the rail as on the page. Done threads fold under "Done · n", and only
 * exist once the API supports resolving.
 */
export function groupThreads(threads: readonly CommentThreadData[]): {
  open: CommentThreadData[];
  done: CommentThreadData[];
} {
  const byWritten = (a: CommentThreadData, b: CommentThreadData) =>
    time(a.comment.createdAt) - time(b.comment.createdAt) || a.id.localeCompare(b.id);
  const open = threads.filter((thread) => !isDone(thread)).sort(byWritten);
  const done = threads.filter((thread) => supportsResolve(thread) && isDone(thread)).sort(byWritten);
  return { open, done };
}

/**
 * How a part reads after "Concept ·": the API writes "The hook" and "Shot 3";
 * the rail says "the hook" and "shot 03". Names ("Instagram caption") keep
 * their capital.
 */
export function partLabel(label: string): string {
  const shot = label.match(/^(Shot|Slide) (\d+)$/);
  if (shot) return `${shot[1].toLowerCase()} ${shot[2].padStart(2, "0")}`;
  if (/^The /.test(label)) return `the ${label.slice(4)}`;
  return label;
}

/** "Concept", "Creative brief", or with an anchor "Concept · the hook". */
export function whereLabel(thread: Pick<CommentThreadData, "source" | "anchor">): string {
  const part = thread.source === "concept" ? "Concept" : "Creative brief";
  return thread.anchor?.label ? `${part} · ${partLabel(thread.anchor.label)}` : part;
}

/** The agency's name beside a teammate; the portal has no field for it. */
export const AGENCY_NAME = "Mohtawa";

function apiEntry(comment: ApiComment): CommentEntry {
  return {
    id: comment.id,
    authorType: comment.authorType,
    authorName: comment.authorName,
    body: comment.body,
    createdAt: comment.createdAt,
    ...(comment.visibility ? { visibility: comment.visibility } : {}),
    authorUserId: comment.authorUserId ?? null,
    mentions: comment.mentions ?? [],
    editedAt: comment.editedAt,
    deleted: comment.deleted,
    intent: comment.clientIntent ?? null,
  };
}

function apiDraft(draft: ApiCommentDraft | null | undefined): ThreadM7Change | null {
  if (!draft || draft.state === "dismissed") return null;
  return { id: draft.id, state: draft.state, label: draft.label, body: draft.body, note: draft.note, error: draft.error ?? null };
}

/** The post's threads as the team reads them (GET plan-slots/:slotId/comments). */
export function threadsFromApi(threads: readonly ApiThread[]): CommentThreadData[] {
  return threads.map((thread) => ({
    id: `${thread.surface}:${thread.id}`,
    source: thread.surface,
    version: thread.version ?? 1,
    comment: apiEntry(thread),
    anchor: thread.anchor
      ? { part: thread.anchor.part, label: thread.anchor.label, ...(thread.anchor.number !== undefined ? { number: thread.anchor.number } : {}) }
      : null,
    anchorKey: thread.anchor?.key ?? null,
    replies: thread.replies.map(apiEntry),
    resolvedAt: thread.resolvedAt,
    resolvedByName: thread.resolvedByName,
    m7Change: apiDraft(thread.m7Change),
    targetId: thread.surface === "concept" ? thread.versionId : thread.briefId,
  }));
}

/** Every comment is for everyone: the team and the client read it. */
export const NEW_COMMENT_VISIBILITY: CommentVisibility = "SHARED";

/** A private comment from before every comment was shared: the team sees it with this line. */
export const PRIVATE_FROM_BEFORE = "Private, from before";

/**
 * No feedback after approval: once the idea and the brief are both approved
 * (the concept CLIENT_APPROVED and the brief ACCEPTED, by the client or by the
 * team for them), the composer closes for the team and the client alike.
 */
export function commentsClosed(slotStatus: string | null | undefined, briefStatus: string | null | undefined): boolean {
  return slotStatus === "CLIENT_APPROVED" && briefStatus === "ACCEPTED";
}

/**
 * The one muted line in the composer's place. `date` is already formatted
 * ("Mon 23 Nov") and left out when the API has none. When the team approved on
 * the client's behalf the line does not say the client did.
 */
export function commentsClosedLine(
  clientName: string | null | undefined,
  date: string | null | undefined,
  approvedBy: "client" | "team" = "client",
): string {
  const on = date ? ` on ${date}` : "";
  if (approvedBy === "team") return `Comments are closed. The idea and the brief were approved${on}.`;
  const client = clientName?.trim() || "The client";
  return `Comments are closed. ${client} approved the idea and the brief${on}.`;
}

/** The later of two moments, or whichever exists. */
export function laterOf(...moments: (string | null | undefined)[]): string | null {
  let best: string | null = null;
  let bestTime = -Infinity;
  for (const iso of moments) {
    if (!iso) continue;
    const at = Date.parse(iso);
    if (Number.isNaN(at) || at <= bestTime) continue;
    best = iso;
    bestTime = at;
  }
  return best;
}

/** "NA" for Noura Alami; the first two letters of a one-word name. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return Array.from(words[0]).slice(0, 2).join("").toUpperCase();
  return (Array.from(words[0])[0] + Array.from(words[words.length - 1])[0]).toUpperCase();
}

/** The line beside a name: the brand's client, or Mohtawa for the team. */
export function orgLabel(authorType: "TEAM" | "CLIENT", clientName: string | null | undefined): string {
  if (authorType === "TEAM") return AGENCY_NAME;
  return clientName?.trim() || "Client";
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mon 10:42" within the week, "12 Nov" when older. */
export function commentTime(iso: string, now: number, timeZone?: string): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return "";
  const date = new Date(at);
  if (now - at >= WEEK_MS) {
    // Built by hand: en-GB now writes "Sept", and the rail says "12 Nov".
    const day = Number(date.toLocaleDateString("en-GB", { day: "numeric", timeZone }));
    const month = Number(date.toLocaleDateString("en-GB", { month: "numeric", timeZone }));
    return `${day} ${MONTHS[month - 1] ?? ""}`.trim();
  }
  const day = date.toLocaleDateString("en-GB", { weekday: "short", timeZone });
  const clock = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone });
  return `${day} ${clock}`;
}

/** The first strong character decides the direction, as dir="auto" does. */
export function isRtlText(text: string): boolean {
  const strong = text.match(/[A-Za-z\u00C0-\u024F\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFC]/);
  return !!strong && /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFC]/.test(strong[0]);
}

/**
 * The rail's one sentence of state, only when it is true: the client already
 * has the brief and reads its comments, but the edits since reach them only
 * with the next send. Otherwise nothing.
 */
export function railSubtitle(
  brief: { version: number; sentVersion: number | null } | null | undefined,
  clientName: string | null | undefined,
): string | null {
  if (!brief || !brief.sentVersion || brief.version <= brief.sentVersion) return null;
  const client = clientName?.trim() || "The client";
  return `${client} sees replies now, changes only when you send version ${brief.version}.`;
}

/**
 * The team rail's sentence under the tabs, only when true. Closed: who
 * approved and when. Otherwise M7's drafts, then what the client sees when
 * the brief has edits since it was sent.
 */
export function teamRailSubtitle({
  brief,
  clientName,
  drafts,
  threads,
  closed,
}: {
  brief: { version: number; sentVersion: number | null } | null | undefined;
  clientName: string | null | undefined;
  /** Open threads carrying an M7 change. */
  drafts: number;
  /** Open threads. */
  threads: number;
  closed: { date: string | null; by: "client" | "team" } | null;
}): string | null {
  const client = clientName?.trim() || "The client";
  if (closed) {
    const on = closed.date ? ` on ${closed.date}` : "";
    return closed.by === "team"
      ? `Comments are closed: the idea and the brief were approved${on}.`
      : `Comments are closed: ${client} approved the idea and the brief${on}.`;
  }
  const lines: string[] = [];
  if (drafts > 0) {
    lines.push(
      drafts === threads
        ? `Creo drafted a change for ${drafts === 1 ? "the comment" : "each comment"}.`
        : `Creo drafted a change for ${drafts} ${drafts === 1 ? "comment" : "comments"}.`,
    );
  }
  const sent = railSubtitle(brief, clientName);
  if (sent) lines.push(sent);
  return lines.length ? lines.join(" ") : null;
}

/** Which part the composer writes on: the page's open tab. */
export function composerSource(phase: PostPhase, briefExists: boolean): CommentSource {
  if (!briefExists) return "concept";
  return phase === "concept" ? "concept" : "brief";
}
